import React from "react";
import { useQueryData, useToast } from "hooks";
import { FormattedMessage } from "react-intl";
import { useMutation } from "@apollo/client";
import { GET_ORGANIZATION, GET_ORGANIZATIONS } from "graphql/queries";
import {
  ARCHIVE_ORGANIZATION,
  RESTORE_ORGANIZATION,
  SET_ORGANIZATION_FEATURE_ENTITLEMENTS,
  SET_ORGANIZATION_CUSTOMER_PORTAL_VERSION,
  UPDATE_ORGANIZATION,
  UPDATE_ORGANIZATION_CONTACT,
} from "graphql/mutations";

export default function useOrganization({ id, includeMissions = false } = {}) {
  const { toast } = useToast();
  const keyName = "organization";
  const { data, loading, error, refetch } = useQueryData({
    queryName: GET_ORGANIZATION,
    keyName,
    skip: !id,
    variables: {
      id,
      includeMissions,
    },
  });

  const [handleUpdate, { loading: updating }] = useMutation(UPDATE_ORGANIZATION_CONTACT, {
    refetchQueries: [GET_ORGANIZATION],
  });

  const [handleOrgUpdate, { loading: updatingOrg }] = useMutation(UPDATE_ORGANIZATION);

  const [handleRemoveOrganization] = useMutation(ARCHIVE_ORGANIZATION, {
    refetchQueries: [GET_ORGANIZATIONS],
  });

  const [handleRestoreOrganization] = useMutation(RESTORE_ORGANIZATION, {
    refetchQueries: [GET_ORGANIZATIONS],
  });

  const [setFeatureEntitlements, { loading: settingFeatureEntitlements }] = useMutation(
    SET_ORGANIZATION_FEATURE_ENTITLEMENTS,
    { refetchQueries: [GET_ORGANIZATION], awaitRefetchQueries: true }
  );

  const [setCustomerPortalVersion, { loading: settingCustomerPortalVersion }] = useMutation(
    SET_ORGANIZATION_CUSTOMER_PORTAL_VERSION,
    { refetchQueries: [GET_ORGANIZATION], awaitRefetchQueries: true }
  );

  /**
   * Flips one portal feature for every client user in the organization.
   *
   * The mutation replaces the baseline outright, so the whole list is sent with this one
   * key added or removed.
   *
   * @param {String}   key      - Feature key
   * @param {Boolean}  enabled
   */
  const toggleFeatureEntitlement = async (key, enabled) => {
    const current = data?.organization?.featureEntitlements || [];
    const features = enabled ? [...new Set([...current, key])] : current.filter((feature) => feature !== key);

    await setFeatureEntitlements({
      variables: {
        input: {
          id: data?.organization?.id,
          params: { features },
        },
      },
      onCompleted: () => toast.success(<FormattedMessage id="useOrganization.FeatureEntitlementsSuccess" />),
      onError: () => toast.error(<FormattedMessage id="useOrganization.FeatureEntitlementsError" />),
    });
  };

  const handleCustomerPortalVersionChange = ({ orgId, customerPortalVersion }) =>
    setCustomerPortalVersion({
      variables: {
        input: {
          id: orgId,
          customerPortalVersion,
        },
      },
      onCompleted: () => toast.success(<FormattedMessage id="useOrganization.CustomerPortalVersionSuccess" />),
      onError: () => toast.error(<FormattedMessage id="useOrganization.CustomerPortalVersionError" />),
    });

  const handleSalesOwnerChange = ({ orgId, salesOwnerId }) => {
    handleOrgUpdate({
      variables: {
        input: {
          id: orgId,
          params: {
            salesOwnerId,
          },
        },
      },
      onCompleted: () => toast.success(<FormattedMessage id="Organization.SalesOwnerChange.MessageSuccess" />),
      onError: () => toast.error(<FormattedMessage id="Organization.SalesOwnerChange.MessageError" />),
    });
  };

  const handlePrimaryChange = ({ id: contactId, firstName, lastName, email }) => {
    handleUpdate({
      variables: {
        input: {
          id,
          params: {
            users: { id: contactId, firstName, lastName, email },
            primaryContact: true,
          },
        },
      },
      onCompleted: () => toast.success(<FormattedMessage id="Organization.MakePrimaryContact.MessageSuccess" />),
      onError: () => toast.error(<FormattedMessage id="Organization.MakePrimaryContact.MessageError" />),
    });
  };

  const removeOrganization = (id) => {
    handleRemoveOrganization({
      variables: {
        input: {
          id,
        },
      },
      onCompleted: () => toast.success(<FormattedMessage id="useOrganization.RemoveSuccess" />),
      onError: (error) => {
        toast.error(error.message);
      },
    });
  };

  const restoreOrganization = (id) => {
    handleRestoreOrganization({
      variables: {
        input: {
          id,
        },
      },
      onCompleted: () => toast.success(<FormattedMessage id="useOrganization.RestoreSuccess" />),
      onError: () => toast.error(<FormattedMessage id="useOrganization.RestoreError" />),
    });
  };

  return {
    loading: loading,
    data: data?.organization,
    error,
    isEditable: true,
    refetch,
    handlePrimaryChange,
    removeOrganization,
    restoreOrganization,
    handleSalesOwnerChange,
    toggleFeatureEntitlement,
    settingFeatureEntitlements,
    handleCustomerPortalVersionChange,
    settingCustomerPortalVersion,
    updatingOrg,
    updating,
    keyName,
  };
}
